// SplatRenderer — renders a Marble world's splat + hidden collider in R3F
// Uses @sparkjsdev/spark for SPZ support (Marble API returns SPZ format)
// Fly-in animation pattern from https://github.com/V4C38/sensai-webxr-worldmodels
import React, { useRef, useEffect } from 'react';
import { useThree, useFrame, extend } from '@react-three/fiber';
import { useXR } from '@react-three/xr';
import { SplatMesh, SplatLoader, SparkRenderer, dyno } from '@sparkjsdev/spark';
import * as THREE from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { getDRACOLoader } from '../../utils/loaderConfig';

// Register Spark components with R3F
extend({ SplatMesh, SparkRenderer });

// Declare JSX types for R3F
declare global {
  namespace JSX {
    interface IntrinsicElements {
      splatMesh: any;
      sparkRenderer: any;
    }
  }
}

interface MarbleWorldRendererProps {
  splatUrl: string;
  /** Pre-built chunked LoD file (.rad + .radc chunks from Spark's build-lod
   *  tool). When set, the world streams progressively with automatic
   *  platform-scaled LoD (500K splat budget on Quest) instead of loading the
   *  full raw splat. */
  radUrl?: string | null;
  colliderUrl?: string | null;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: [number, number, number];
  groundPlaneOffset?: number | null;
  showCollider?: boolean;
  onColliderLoaded?: (mesh: THREE.Group) => void;
  onClick?: () => void;
}

// Shared SparkRenderer singleton with reference counting.
// Multiple consumers (marble world + content splats) can mount EnsureSparkRenderer;
// the renderer is only destroyed when the LAST consumer unmounts.
let sparkInstance: SparkRenderer | null = null;
let sparkRefCount = 0;

// Runtime access for quality throttling (VRBuildQualityProfile)
export function getSparkInstance(): SparkRenderer | null {
  return sparkInstance;
}

export const EnsureSparkRenderer: React.FC = () => {
  const { scene, gl, camera } = useThree();

  useEffect(() => {
    sparkRefCount += 1;

    if (!sparkInstance) {
      // Settings sourced from sensai-webxr-worldmodels reference implementation:
      //   behindFoveate: 0.1  — gentle; 8 caused "melting" on camera rotation
      //   outsideFoveate: 0.3 — mild peripheral culling
      //   renderOrder: -10    — splats render behind UI / overlays
      // Quest Browser masquerades as desktop Linux (no "Android"/"Mobi" in the
      // UA), so Spark's isMobile() heuristic gives it the DESKTOP paged-splat
      // pool: 16.7M splats ≈ 400-600MB of CPU+GPU buffers — fatal on a
      // headset with ~2GB tab memory. Cap the pool at 4M splats (64 pages,
      // ~100MB) on Quest. Spark's own splat-render budget (500K on Quest)
      // detects the headset correctly via "Oculus" in the UA.
      const isQuest = !!navigator.xr && /Oculus/i.test(navigator.userAgent);
      // Tuning knobs via URL params (no redeploy needed):
      //   ?lodScale=0.5  — halve the LoD splat render budget (default budget:
      //                    500K on Quest, 2.5M on desktop)
      //   ?poolPages=32  — paged-splat pool size in 65536-splat pages
      const params = new URLSearchParams(window.location.search);
      const lodScale = parseFloat(params.get('lodScale') || '') || undefined;
      const poolPages =
        parseInt(params.get('poolPages') || '', 10) || (isQuest ? 64 : 128);
      const spark = new SparkRenderer({
        renderer: gl,
        sortRadial: true,
        behindFoveate: 0.1,
        // NOTE: foveation params (coneFoveate/behindFoveate) only affect LoD
        // splats — they are inert while meshes load with lod: false.
        // ("outsideFoveate" does not exist in Spark v2.0.0-preview; the real
        // knob is coneFoveate.)
        coneFoveate: 0.3,
        // Render each splat quad to 2.2 std-devs instead of the √8≈2.83
        // default: the Gaussian fringe beyond ~2σ is nearly transparent but
        // still pays full alpha-blend cost — this trims ~40% of filled
        // pixels. Splat scenes are fill-bound, so this is close to a free
        // ~1.6x on GPU time.
        maxStdDev: 2.2,
        // Pool buffers scale with pages (~1.5MB each CPU+GPU). Spark's
        // default is 256 pages (16.7M splats) on desktop because the UA
        // sniffing treats Quest Browser as desktop Linux; our worlds are
        // ≤4M LoD splats, so cap it: 64 pages (~100MB) on Quest, 128
        // (~200MB) on desktop.
        maxPagedSplats: poolPages * 65536,
        // Spark's desktop LoD budget (2.5M splats) assumes a discrete GPU.
        // Perf capture on an M3 Pro showed ~1.75M splats rendered = 3.5M
        // transparent triangles = 78ms GPU (11fps) with the CPU idle at 1ms.
        // 1M is the right desktop ceiling for integrated/Apple-silicon GPUs;
        // Quest keeps Spark's own 500K auto-budget. ?lodScale still scales it.
        ...(isQuest ? {} : { lodSplatCount: 1_000_000 }),
        ...(lodScale ? { lodSplatScale: lodScale } : {}),
      });
      spark.renderOrder = -10;
      console.log(
        `[Spark] pool=${poolPages} pages, lodSplatScale=${lodScale ?? 1}, quest=${isQuest}`
      );
      scene.add(spark);
      sparkInstance = spark;
      console.log('[Spark] SparkRenderer added to scene');
    }

    return () => {
      sparkRefCount -= 1;
      if (sparkRefCount <= 0 && sparkInstance) {
        scene.remove(sparkInstance);
        try { sparkInstance.dispose?.(); } catch (e) {}
        sparkInstance = null;
        sparkRefCount = 0;
        console.log('[Spark] SparkRenderer removed from scene');
      }
    };
    // IMPORTANT: camera is NOT a dependency — WorldBuilder swaps cameras
    // (orbit <-> first-person), and recreating the SparkRenderer on camera
    // change disposes the LoD worker mid-stream ("Worker terminate") and
    // re-downloads every splat.
  }, [scene, gl]);

  // SparkJS driveLod() clones the camera every frame. R3F's camera can carry
  // extra children / closures that crash the clone chain — patch it to return
  // a lightweight copy with only the matrix/projection data Spark needs.
  // Separate effect so camera swaps re-patch without touching the renderer.
  useEffect(() => {
    const cam = camera as THREE.PerspectiveCamera & { __sparkClonePatched?: boolean };
    if (!cam.__sparkClonePatched) {
      cam.__sparkClonePatched = true;
      cam.clone = function (this: THREE.PerspectiveCamera) {
        const c = new THREE.PerspectiveCamera();
        c.projectionMatrix.copy(this.projectionMatrix);
        c.projectionMatrixInverse.copy(this.projectionMatrixInverse);
        c.matrixWorld.copy(this.matrixWorld);
        c.matrixWorldInverse.copy(this.matrixWorldInverse);
        return c;
      };
    }
  }, [camera]);

  return null;
};

// ---- Fly-in animator (port of GaussianSplatAnimator from sensai-webxr-worldmodels) ----
// Uses dyno GLSL modifiers so all per-splat work runs on GPU.
// CPU only updates a single progress float each frame.

const FLY_IN_DURATION = 1.5;  // seconds
const FLY_IN_SPREAD = 0.4;    // stagger window (0 = all at once, 1 = fully sequential)

class SplatFlyInAnimator {
  readonly splat: SplatMesh;
  private progress: ReturnType<typeof dyno.dynoFloat>;
  private _animating = false;
  private direction: 'in' | 'out' = 'in';
  private startTime = 0;
  private duration = FLY_IN_DURATION;
  private resolve: (() => void) | null = null;

  constructor(splat: SplatMesh) {
    this.splat = splat;
    this.progress = dyno.dynoFloat(0.0);
  }

  /** Apply GLSL modifier to the mesh — call once after mesh is created. */
  apply(): void {
    const progress = this.progress;
    const spreadVal = FLY_IN_SPREAD.toFixed(4);

    const modifier = dyno.dynoBlock(
      { gsplat: dyno.Gsplat },
      { gsplat: dyno.Gsplat },
      ({ gsplat }) => {
        const node = new dyno.Dyno({
          inTypes: { gsplat: dyno.Gsplat, progress: 'float' },
          outTypes: { gsplat: dyno.Gsplat },
          globals: () => [
            dyno.unindent(`
              const float SPREAD = ${spreadVal};
              float hashP(vec3 p) {
                p = fract(p * 0.3183099 + 0.1);
                p *= 17.0;
                return fract(p.x * p.y * p.z * (p.x + p.y + p.z));
              }
              vec3 hashP3(vec3 p) {
                p = fract(p * vec3(0.1031, 0.1030, 0.0973));
                p += dot(p, p.yxz + 33.33);
                return fract((p.xxy + p.yxx) * p.zyx);
              }
              float subtleEase(float t) {
                return mix(t, t * t * (3.0 - 2.0 * t), 0.3);
              }
              float localProgress(float g, float rand) {
                float start = rand * (1.0 - SPREAD);
                return subtleEase(clamp((g - start) / SPREAD, 0.0, 1.0));
              }
              vec3 turbulence(vec3 pos, vec3 r, float lp) {
                float str = (1.0 - lp) * (1.0 - lp);
                float phase = r.x * 6.28318;
                float spd = 2.0 + r.y * 1.5;
                float t = lp * spd + phase;
                vec3 turb = vec3(
                  sin(t * 2.3 + pos.z * 1.5) * 0.05 + sin(t * 4.1) * 0.025,
                  sin(t * 1.7 + pos.x * 1.2) * 0.02,
                  sin(t * 3.1 + pos.x * 1.8) * 0.05 + cos(t * 2.7) * 0.025
                );
                vec3 rOff = vec3((r.x - 0.5) * 0.06, 0.0, (r.z - 0.5) * 0.06);
                return (turb + rOff) * str;
              }
            `),
          ],
          statements: ({ inputs, outputs }) =>
            dyno.unindentLines(`
              ${outputs.gsplat} = ${inputs.gsplat};
              vec3 c = ${inputs.gsplat}.center;
              float rand = hashP(c * 10.0);
              vec3 rand3 = hashP3(c * 7.0);
              float lp = localProgress(${inputs.progress}, rand);
              vec3 startPos = c + vec3(0.0, -2.0, 0.0);
              vec3 turb = turbulence(c, rand3, lp);
              ${outputs.gsplat}.center = mix(startPos, c, lp) + turb;
              ${outputs.gsplat}.scales *= 0.3 + 0.7 * lp;
              ${outputs.gsplat}.rgba.a *= lp;
            `),
        });
        return { gsplat: node.apply({ gsplat, progress }).gsplat };
      },
    );

    this.splat.objectModifier = modifier;
    this.splat.updateGenerator();
  }

  animateIn(duration = FLY_IN_DURATION): Promise<void> {
    return this._start('in', duration);
  }

  stop(): void {
    if (!this._animating) return;
    this._animating = false;
    const r = this.resolve;
    this.resolve = null;
    r?.();
  }

  dispose(): void {
    this.stop();
  }

  tick(): void {
    if (!this._animating) return;
    const elapsed = (performance.now() - this.startTime) / 1000;
    const t = Math.min(elapsed / this.duration, 1);
    this.progress.value = this.direction === 'in' ? t : 1 - t;
    this.splat.updateVersion();
    if (t >= 1) {
      this._animating = false;
      // Remove modifier after fly-in — no per-frame GPU overhead and no shader
      // interaction with Spark's sort order changes during camera rotation.
      if (this.direction === 'in') {
        this.splat.objectModifier = undefined;
        this.splat.updateGenerator();
      }
      const r = this.resolve;
      this.resolve = null;
      r?.();
    }
  }

  private _start(direction: 'in' | 'out', duration: number): Promise<void> {
    this.stop();
    this.direction = direction;
    this.duration = duration;
    this.startTime = performance.now();
    this._animating = true;
    return new Promise<void>((res) => { this.resolve = res; });
  }
}

// ---- Main renderer component ----

const MarbleWorldRenderer: React.FC<MarbleWorldRendererProps> = ({
  splatUrl,
  radUrl,
  colliderUrl,
  position = [0, 0, 0],
  rotation = [0, 0, 0],
  scale = [1, 1, 1],
  groundPlaneOffset,
  showCollider = false,
  onColliderLoaded,
  onClick,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const splatRef = useRef<SplatMesh | null>(null);
  const colliderRef = useRef<THREE.Group | null>(null);
  const animatorRef = useRef<SplatFlyInAnimator | null>(null);
  const isPresenting = useXR((s) => !!s.session);
  const isPresentingRef = useRef(isPresenting);

  // Track VR entry to replay animation
  useEffect(() => {
    if (isPresenting && !isPresentingRef.current) {
      // Just entered VR — replay fly-in
      animatorRef.current?.animateIn();
    }
    isPresentingRef.current = isPresenting;
  }, [isPresenting]);

  // Advance animation every frame
  useFrame(() => {
    animatorRef.current?.tick();
  });

  // Load splat. Two paths:
  //  - radUrl (preferred): pre-built chunked LoD tree from build-lod, streamed
  //    with paged: true. Coarse approximation appears almost instantly and
  //    refines as chunks download; Spark auto-budgets rendered splats per
  //    platform (500K on Quest). Progressive refinement IS the entrance
  //    animation, so the fly-in is skipped.
  //  - splatUrl (legacy): full raw splat via SplatLoader, no LoD.
  useEffect(() => {
    if (!groupRef.current) return;
    let disposed = false;

    const loadSplat = async () => {
      try {
        let mesh: SplatMesh;
        if (radUrl) {
          mesh = new SplatMesh({ url: radUrl, paged: true });
          console.log('[MarbleWorld] Streaming LoD splat (paged):', radUrl);
        } else {
          const loader = new SplatLoader();
          const packedSplats = await loader.loadAsync(splatUrl);
          if (disposed) return;

          // lod: false — skip LoD tree. createLodSplats() takes 18–28 s for ~2M-splat
          // files and increases the primitive count (1.92M → 2.72M), causing GPU
          // memory pressure and WebGL context loss. Precompute LoD server-side instead.
          mesh = new SplatMesh({ packedSplats, lod: false });
          console.log('[MarbleWorld] Splat loaded via SplatLoader:', splatUrl);
        }
        // SH degree 1 = directional color variation (view-dependent highlights).
        // Degree 0 is too flat; degree 2+ is too expensive on Quest.
        mesh.maxSh = 1;
        // Render behind UI / overlays (matches sensai reference implementation)
        mesh.renderOrder = -10;
        // Match World Labs coordinate convention
        mesh.quaternion.set(1, 0, 0, 0);

        splatRef.current = mesh;
        groupRef.current?.add(mesh);

        // Fly-in shader modifier (legacy full-splat path only)
        if (!disposed && !radUrl) {
          const animator = new SplatFlyInAnimator(mesh);
          animator.apply();
          animatorRef.current = animator;
          animator.animateIn();
        }
      } catch (err) {
        console.error('[MarbleWorld] Splat load failed:', err);
      }
    };

    loadSplat();

    return () => {
      disposed = true;
      animatorRef.current?.dispose();
      animatorRef.current = null;
      try {
        if (groupRef.current && splatRef.current) {
          groupRef.current.remove(splatRef.current);
        }
        splatRef.current?.dispose();
      } catch (e) {}
      splatRef.current = null;
    };
  }, [splatUrl, radUrl]);

  // Load collider mesh (hidden GLB for physics/raycasting)
  useEffect(() => {
    if (!colliderUrl || !groupRef.current) return;
    let disposed = false;

    const loader = new GLTFLoader();
    loader.setDRACOLoader(getDRACOLoader());
    loader.load(colliderUrl, (gltf) => {
      if (disposed) return;
      const collider = gltf.scene;

      collider.traverse((obj: any) => {
        if (obj.isMesh) {
          obj.userData.isCollider = true;
          obj.userData.isMarbleCollider = true;

          if (showCollider) {
            // Debug mode: show wireframe
            obj.visible = true;
            obj.material = new THREE.MeshBasicMaterial({
              color: 0x00ff00,
              wireframe: true,
              transparent: true,
              opacity: 0.3,
            });
          } else {
            // Hidden from rendering. visible=false still allows Mesh.raycast()
            // when the object is passed directly to raycaster.intersectObjects().
            obj.visible = false;
            obj.layers.enable(1); // Also on layer 1 for VR collision
            // Dispose original material to free GPU memory
            if (obj.material) {
              if (Array.isArray(obj.material)) obj.material.forEach((m: THREE.Material) => m.dispose());
              else obj.material.dispose();
            }
            obj.material = new THREE.MeshBasicMaterial({ visible: false });
          }
        }
      });

      colliderRef.current = collider;
      groupRef.current?.add(collider);
      onColliderLoaded?.(collider);
      console.log('[MarbleWorld] Collider loaded:', colliderUrl);
    }, undefined, (err) => {
      console.error('[MarbleWorld] Collider failed:', err);
    });

    return () => {
      disposed = true;
      if (colliderRef.current && groupRef.current) {
        groupRef.current.remove(colliderRef.current);
      }
      // Dispose geometry + materials to free GPU memory (prevents context loss on navigation)
      colliderRef.current?.traverse((child: any) => {
        if (child.isMesh) {
          child.geometry?.dispose();
          const mats = Array.isArray(child.material) ? child.material : [child.material];
          mats.forEach((m: THREE.Material) => m?.dispose());
        }
      });
      colliderRef.current = null;
    };
  }, [colliderUrl, showCollider]);

  // Ground plane offset adjusts Y within the local group
  const yOffset = groundPlaneOffset ? -groundPlaneOffset : 0;

  // Coordinate correction now handled by mesh.quaternion.set(1,0,0,0) on the SplatMesh
  // Group rotation is purely user-applied
  const correctedRotation: [number, number, number] = [
    rotation?.[0] || 0,
    rotation?.[1] || 0,
    rotation?.[2] || 0,
  ];

  return (
    <>
      <EnsureSparkRenderer />
      <group
        ref={groupRef}
        position={position ? [position[0], position[1] + yOffset, position[2]] : [0, yOffset, 0]}
        rotation={correctedRotation}
        scale={scale}
        onClick={onClick}
      />
    </>
  );
};

export default MarbleWorldRenderer;
